import * as React from "react"
import { Head, router, useForm, usePage } from "@inertiajs/react"
import { RotateCcw, Save, Wand2 } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { SettingsLayout } from "@/components/settings/SettingsLayout"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Select } from "@/components/ui/select"
import type { PageProps } from "@/types/inertia"

type ClassifierCategory = {
  readonly label: string
  readonly auto_archive: boolean
  readonly positive: boolean
  readonly sibling_action: string
  readonly description: string
}

type ClassifierProps = {
  readonly prompt_template: string
  readonly categories: readonly ClassifierCategory[]
  readonly sibling_action_values: readonly string[]
}

type ClassifierForm = {
  prompt_template: string
  categories: Record<
    string,
    {
      auto_archive: boolean
      positive: boolean
      sibling_action: string
      description: string
    }
  >
}

type TestState =
  | { readonly status: "idle" }
  | { readonly status: "loading" }
  | { readonly status: "success"; readonly result: Record<string, unknown> }
  | { readonly status: "error"; readonly message: string }

export default function ClassifierSettings({
  prompt_template,
  categories,
  sibling_action_values,
}: ClassifierProps) {
  const { props } = usePage<PageProps<ClassifierProps>>()
  const description =
    "Edit ReplyOps classifier prompt flags and test the saved classifier against pasted reply text."
  const form = useForm<ClassifierForm>({
    prompt_template,
    categories: Object.fromEntries(
      categories.map((category) => [
        category.label,
        {
          auto_archive: category.auto_archive,
          positive: category.positive,
          sibling_action: category.sibling_action,
          description: category.description,
        },
      ]),
    ),
  })
  const [sample, setSample] = React.useState("")
  const [testState, setTestState] = React.useState<TestState>({ status: "idle" })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    form.patch("/settings/classifier", { preserveScroll: true })
  }

  const runTest = async () => {
    setTestState({ status: "loading" })
    try {
      const response = await fetch("/settings/classifier/test", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ text: sample }),
      })
      const data: unknown = await response.json()
      if (!response.ok) {
        setTestState({ status: "error", message: "Classifier test failed." })
        return
      }
      if (isResultPayload(data)) {
        setTestState({ status: "success", result: data.result })
      } else {
        setTestState({ status: "error", message: "Unexpected classifier response." })
      }
    } catch (error) {
      if (error instanceof Error) {
        setTestState({ status: "error", message: error.message })
        return
      }
      setTestState({ status: "error", message: "Classifier test failed." })
    }
  }

  return (
    <>
      <Head title="Classifier Settings">
        <meta name="description" content={description} />
        <meta property="og:title" content="Classifier Settings" />
        <meta property="og:description" content={description} />
      </Head>
      <SettingsLayout wide>
        <div className="flex flex-col gap-6">
          <header>
            <h1>Classifier Settings</h1>
            <p className="mt-2">Tune fixed buckets without arming live mutations.</p>
          </header>

          {props.flash.notice && (
            <div className="rounded-md border border-accent bg-accent-faded px-4 py-3 text-sm text-accent-display">
              {props.flash.notice}
            </div>
          )}

          <form onSubmit={submit} className="space-y-6">
            <section className="rounded-lg border border-hairline bg-page p-6">
              <label htmlFor="prompt_template">Prompt template</label>
              <textarea
                id="prompt_template"
                className="form-control form-control-textarea mt-2 min-h-72 font-mono text-sm"
                value={form.data.prompt_template}
                onChange={(event) =>
                  form.setData("prompt_template", event.target.value)
                }
              />
            </section>

            <section className="overflow-hidden rounded-lg border border-hairline bg-page">
              <div className="grid grid-cols-[190px_110px_90px_190px_1fr] gap-3 border-b border-hairline bg-surface px-4 py-3 text-sm text-ink-muted">
                <div>Bucket</div>
                <div>Auto archive</div>
                <div>Positive</div>
                <div>Sibling action</div>
                <div>Description</div>
              </div>
              <div className="divide-y divide-hairline">
                {categories.map((category) => {
                  const row = form.data.categories[category.label]
                  return (
                    <div
                      key={category.label}
                      className="grid gap-3 px-4 py-3 lg:grid-cols-[190px_110px_90px_190px_1fr]"
                    >
                      <div className="flex items-center">
                        <Badge tone={category.positive ? "signal" : "muted"}>
                          {formatLabel(category.label)}
                        </Badge>
                      </div>
                      <label
                        htmlFor={`auto_archive_${category.label}`}
                        className="flex items-center gap-2 font-normal text-ink-body"
                      >
                        <Checkbox
                          id={`auto_archive_${category.label}`}
                          checked={row?.auto_archive ?? false}
                          onChange={(event) =>
                            updateCategory(form, category.label, {
                              auto_archive: event.target.checked,
                            })
                          }
                        />
                        <span>Archive</span>
                      </label>
                      <label
                        htmlFor={`positive_${category.label}`}
                        className="flex items-center gap-2 font-normal text-ink-body"
                      >
                        <Checkbox
                          id={`positive_${category.label}`}
                          checked={row?.positive ?? false}
                          onChange={(event) =>
                            updateCategory(form, category.label, {
                              positive: event.target.checked,
                            })
                          }
                        />
                        <span>Yes</span>
                      </label>
                      <Select
                        value={row?.sibling_action ?? "none"}
                        onChange={(event) =>
                          updateCategory(form, category.label, {
                            sibling_action: event.target.value,
                          })
                        }
                        aria-label={`${category.label} sibling action`}
                      >
                        {sibling_action_values.map((value) => (
                          <option key={value} value={value}>
                            {formatLabel(value)}
                          </option>
                        ))}
                      </Select>
                      <input
                        className="form-control"
                        value={row?.description ?? ""}
                        onChange={(event) =>
                          updateCategory(form, category.label, {
                            description: event.target.value,
                          })
                        }
                        aria-label={`${category.label} description`}
                      />
                    </div>
                  )
                })}
              </div>
            </section>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() =>
                  router.post("/settings/classifier/reset", undefined, {
                    preserveScroll: true,
                  })
                }
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </Button>
              <Button type="submit" disabled={form.processing}>
                <Save className="h-4 w-4" />
                Save
              </Button>
            </div>
          </form>

          <section className="rounded-lg border border-hairline bg-page p-6">
            <h2>Test classifier</h2>
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div>
                <label htmlFor="classifier_test_text">Reply text</label>
                <textarea
                  id="classifier_test_text"
                  className="form-control form-control-textarea mt-2 min-h-48"
                  value={sample}
                  onChange={(event) => setSample(event.target.value)}
                />
                <Button
                  type="button"
                  className="mt-3"
                  disabled={!sample.trim() || testState.status === "loading"}
                  onClick={() => {
                    void runTest()
                  }}
                >
                  <Wand2 className="h-4 w-4" />
                  Test
                </Button>
              </div>
              <div className="rounded-lg border border-hairline bg-surface p-4">
                {testState.status === "success" ? (
                  <pre className="overflow-auto text-sm">
                    {JSON.stringify(testState.result, null, 2)}
                  </pre>
                ) : testState.status === "error" ? (
                  <p className="text-danger-display">{testState.message}</p>
                ) : testState.status === "loading" ? (
                  <p>Testing classifier...</p>
                ) : (
                  <p>Paste a reply to preview the raw classifier JSON.</p>
                )}
              </div>
            </div>
          </section>
        </div>
      </SettingsLayout>
    </>
  )
}

function updateCategory(
  form: ReturnType<typeof useForm<ClassifierForm>>,
  label: string,
  patch: Partial<ClassifierForm["categories"][string]>,
) {
  const existing = form.data.categories[label]
  if (!existing) return

  form.setData("categories", {
    ...form.data.categories,
    [label]: { ...existing, ...patch },
  })
}

function formatLabel(value: string) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}

function csrfToken() {
  if (typeof document === "undefined") return ""

  const token = document.querySelector<HTMLMetaElement>("meta[name='csrf-token']")
  return token?.content ?? ""
}

function isResultPayload(value: unknown): value is { result: Record<string, unknown> } {
  return (
    typeof value === "object" &&
    value !== null &&
    "result" in value &&
    typeof value.result === "object" &&
    value.result !== null
  )
}
